import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Activity, ArrowRight, CheckCircle2, Eye, EyeOff, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SetupAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // 1 = verify, 2 = set password, 3 = success
  
  // Get email and temp password from URL params
  const email = searchParams.get('email') || '';
  const tempPassword = searchParams.get('token') || '';
  
  const [formData, setFormData] = useState({
    email: email,
    tempPassword: tempPassword,
    newPassword: '',
    confirmPassword: ''
  });
  
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Pre-fill email from URL
    if (email) {
      setFormData(prev => ({ ...prev, email: email }));
    }
    if (tempPassword) {
      setFormData(prev => ({ ...prev, tempPassword: tempPassword }));
    }
  }, [email, tempPassword]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Try to login with temp password to verify the account
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.tempPassword
      });
      
      setUserData(response.data);
      setStep(2);
      toast.success('Account verified! Now set your new password.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or temporary password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Change password
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: formData.tempPassword,
          new_password: formData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${userData.access_token}` }
        }
      );
      
      setStep(3);
      toast.success('Password set successfully!');
      
      // Auto-redirect after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-white font-semibold text-xl tracking-tight font-[Manrope]">Pulse</h1>
            <p className="text-slate-500 text-sm">Players Health</p>
          </div>
        </div>
        
        <div>
          <h2 className="text-4xl font-bold text-white font-[Manrope] tracking-tight mb-4">
            Welcome to Your Team
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed">
            Complete your account setup to access real-time KPI dashboards and collaborate with your executive team.
          </p>
          
          {/* Progress Steps */}
          <div className="mt-8 space-y-4">
            <div className={`flex items-center gap-3 ${step >= 1 ? 'text-white' : 'text-slate-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step > 1 ? 'bg-green-500' : step === 1 ? 'bg-blue-600' : 'bg-slate-700'}`}>
                {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : '1'}
              </div>
              <span>Verify your account</span>
            </div>
            <div className={`flex items-center gap-3 ${step >= 2 ? 'text-white' : 'text-slate-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step > 2 ? 'bg-green-500' : step === 2 ? 'bg-blue-600' : 'bg-slate-700'}`}>
                {step > 2 ? <CheckCircle2 className="w-5 h-5" /> : '2'}
              </div>
              <span>Set your password</span>
            </div>
            <div className={`flex items-center gap-3 ${step >= 3 ? 'text-white' : 'text-slate-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 3 ? 'bg-green-500' : 'bg-slate-700'}`}>
                {step === 3 ? <CheckCircle2 className="w-5 h-5" /> : '3'}
              </div>
              <span>Start using Pulse</span>
            </div>
          </div>
        </div>

        <div className="text-slate-500 text-sm">
          © {new Date().getFullYear()} Players Health. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Setup Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-slate-900 font-semibold text-xl tracking-tight font-[Manrope]">Pulse</h1>
              <p className="text-slate-500 text-sm">Players Health</p>
            </div>
          </div>

          {/* Step 1: Verify Account */}
          {step === 1 && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 font-[Manrope]">Set Up Your Account</h2>
                <p className="text-slate-500 mt-1">Enter your email and temporary password from the invitation email</p>
              </div>

              <form onSubmit={handleVerify} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@playershealth.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="setup-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tempPassword">Temporary Password</Label>
                  <p className="text-xs text-slate-500">This was included in your invitation email</p>
                  <Input
                    id="tempPassword"
                    type="text"
                    placeholder="Enter temporary password"
                    value={formData.tempPassword}
                    onChange={(e) => setFormData({ ...formData, tempPassword: e.target.value })}
                    required
                    data-testid="setup-temp-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                  data-testid="setup-verify-btn"
                >
                  {isLoading ? 'Verifying...' : 'Verify & Continue'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Set New Password */}
          {step === 2 && (
            <>
              <div className="mb-8">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Account verified!</span>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 font-[Manrope]">Create Your Password</h2>
                <p className="text-slate-500 mt-1">Choose a strong password for your account</p>
              </div>

              <form onSubmit={handleSetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      required
                      minLength={8}
                      className="pr-10"
                      data-testid="setup-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Must be at least 8 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      className="pr-10"
                      data-testid="setup-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                  data-testid="setup-password-btn"
                >
                  {isLoading ? 'Setting password...' : 'Set Password & Continue'}
                  <Lock className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 font-[Manrope] mb-2">You're All Set!</h2>
              <p className="text-slate-500 mb-6">Your account is ready. Redirecting you to login...</p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full"
                data-testid="setup-goto-login-btn"
              >
                Go to Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {step !== 3 && (
            <p className="mt-6 text-center text-sm text-slate-500">
              Already set up your account?{' '}
              <a href="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Sign in
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupAccount;
